import { Box, EmployeeAvatar, SectionHeader, useBoxStyle , AHSeparator, ListItem } from "arkh";
import { useFragment, graphql } from "react-relay";
import { SectionList } from "react-native";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { useMemo } from "react";

import { StoreUserRoleAndEmailTag } from "../UserAdmin/StoreUserRoleAndEmailTag";
import { WhitelistUsersList_Store$key } from "./__generated__/WhitelistUsersList_Store.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { FormattedMessage } from "react-intl";

export function WhitelistUsersList(props: {
  store: WhitelistUsersList_Store$key;
  whitelistUserIds: string[];
  setWhitelistUserIds: (whitelistUserIds: string[]) => void;
}) {
  const store = useFragment(
    graphql`
      fragment WhitelistUsersList_Store on Store {
        users {
          id
          role
          ...StoreUserRoleAndEmailTag_storeUser
          user {
            id
          }
          employee {
            id
            name
            ...Avatar_employee
          }
        }
      }
    `,
    props.store
  );

  const intl_tokens = useIntlTokens();

  const contentContainerStyle = useBoxStyle({
    space: "medium",
  });

  const users = useMemo(() => {
    return store.users ?? [];
  }, [store]);

  const sections = useMemo(() => {
    const _sections = [
      {
        key: "other",
        data: users.filter((user) => user.role !== "ADMIN"),
      },
    ];
    const adminUsers = users.filter((user) => user.role === "ADMIN");
    if (adminUsers.length > 0) {
      _sections.push({
        key: "admin",
        data: adminUsers,
      });
    }
    return _sections;
  }, [users]);

  return (
    <SectionList
      sections={sections}
      ItemSeparatorComponent={AHSeparator}
      keyExtractor={keyExtractorId}
      contentContainerStyle={contentContainerStyle}
      renderSectionHeader={({ section }) => {
        if (section.key === "other") {
          return null;
        }
        return (
          <Box spaceTop="medium">
            <SectionHeader>
              <SectionHeader.Title>{intl_tokens.Admins}</SectionHeader.Title>
              <SectionHeader.Description>
                <FormattedMessage
                  defaultMessage="Admins have access to all locked teams"
                  description="WhitelistUsersList_admins_description"
                />
              </SectionHeader.Description>
            </SectionHeader>
          </Box>
        );
      }}
      renderItem={({ item, index, section }) => {
        const isSelected = props.whitelistUserIds.includes(item.user.id);

        return (
          <ListItem
            disabled={item.role === "ADMIN"}
            isFirst={index === 0}
            isLast={index === section.data.length - 1}
            left={
              item.employee ? (
                <EmployeeAvatar employee={item.employee} size="large" />
              ) : null
            }
            right={
              <ListItem.SingleSelectIndicator
                selected={item.role === "ADMIN" ? true : isSelected}
                disabled={item.role === "ADMIN"}
              />
            }
            onPress={() => {
              props.setWhitelistUserIds(
                isSelected
                  ? props.whitelistUserIds.filter((id) => id !== item.user.id)
                  : [...props.whitelistUserIds, item.user.id]
              );
            }}
            footer={<StoreUserRoleAndEmailTag storeUser={item} />}
          >
            <ListItem.Title>{item.employee?.name}</ListItem.Title>
          </ListItem>
        );
      }}
    />
  );
}
